import { FC, useCallback, useMemo } from "react";

import { customAccommodationRoomTypesSelectorCreator } from "Bands/state";
import {
  AccommodationRoomType,
  IAccommodationBlock,
  IAccommodationRoomType,
  IAccommodationRoomTypeParams,
} from "Bands/types";
import { useSelector } from "react-redux";
import { copy } from "shared/assets";
import { IActionDropdownItem, Table, TableColumn } from "shared/components";
import { IStatus } from "shared/types";
import { mapUserPermissions } from "shared/utils";

const { accommodationBlock: blockCopy } = copy;

interface AccommodationRoomTableProps {
  setShowEditModal: (accommodationRoom?: IAccommodationRoomType) => void;
  setShowDeleteModal: (accommodationRoom?: IAccommodationRoomType) => void;
  block: IAccommodationBlock;
}

export const AccommodationRoomTable: FC<AccommodationRoomTableProps> = ({
  block,
  setShowEditModal,
  setShowDeleteModal,
}) => {
  const customAccommodationRoomsSelector = useMemo(
    () => customAccommodationRoomTypesSelectorCreator(block.id),
    [block.id]
  );

  const accommodationRooms = useSelector(customAccommodationRoomsSelector);
  const getRowActions = useCallback(
    (accommodationRoomId: string) => {
      const findAccommodationRoom = (
        findId: string
      ): IAccommodationRoomType | undefined => {
        return accommodationRooms.find((room) => room.id === findId);
      };

      const accommodationRoom = findAccommodationRoom(accommodationRoomId);

      if (!accommodationRoom) return [];

      const permissions = mapUserPermissions(block.permissions || []);

      let actions: IActionDropdownItem[] = [];

      if (permissions.EDIT) {
        actions.push({
          label: "Edit",
          handleClick: () => {
            setShowEditModal(accommodationRoom);
          },
          dataTestId: "edit_accommodation_room_action",
        });
      }

      if (permissions.DELETE) {
        actions.push({
          label: "Delete room",
          handleClick: () => {
            setShowDeleteModal(accommodationRoom);
          },
          dataTestId: "delete_accommodation_room_action",
          status: IStatus.error,
        });
      }

      return actions;
    },
    [
      block.permissions,
      accommodationRooms,
      setShowDeleteModal,
      setShowEditModal,
    ]
  );

  return (
    <Table
      data={accommodationRooms}
      columns={getColumns()}
      getRowActions={getRowActions}
      emptyCopy={blockCopy.create.roomsBlock.placeholders.emptyRoomsTable}
    />
  );
};

const getColumns = (): TableColumn[] => [
  {
    Header: blockCopy.create.roomsBlock.fields.name,
    accessor: "name",
    Cell: ({ row }) => {
      const room = row.original as IAccommodationRoomTypeParams;

      return (
        AccommodationRoomType[
          room.name as keyof typeof AccommodationRoomType
        ] || room.name
      );
    },
  },
  {
    Header: blockCopy.create.roomsBlock.fields.quantity,
    accessor: "quantity",
  },
  {
    Header: blockCopy.create.roomsBlock.fields.peoplePerRoom,
    accessor: "peoplePerRoom",
  },
];
